/**
 * Lightweight analytics helper — wraps PostHog (already loaded in
 * /app/frontend/public/index.html). Defensive: silently no-ops if PostHog
 * hasn't loaded yet (slow network, ad-blocker, opted-out user) so it can
 * never block conversion or break the click navigation.
 *
 * No new dependencies. No popups. Respects existing consent setup.
 */

/** Detect whether the current viewport is mobile-class. Cheap one-liner. */
function detectDevice() {
  if (typeof window === "undefined" || !window.matchMedia) return "unknown";
  return window.matchMedia("(max-width: 767px)").matches ? "mobile" : "desktop";
}

/**
 * Fire an analytics event.
 * Auto-attaches: page_path, device, timestamp_iso, cta_label.
 *
 * @param {string} eventName  e.g. "landing_hero_cta_clicked"
 * @param {string} ctaLabel   The visible label of the clicked CTA.
 * @param {Object} [extras]   Any extra event-specific properties.
 */
export function trackCta(eventName, ctaLabel, extras = {}) {
  try {
    if (typeof window === "undefined" || !window.posthog) return;
    if (typeof window.posthog.capture !== "function") return;
    window.posthog.capture(eventName, {
      cta_label: ctaLabel,
      page_path: window.location?.pathname || "/",
      device: detectDevice(),
      timestamp_iso: new Date().toISOString(),
      ...extras,
    });
  } catch {
    // Analytics must never break the user flow.
  }
}

/**
 * Generic event helper — same auto-attached context (page_path, device,
 * timestamp_iso) but no required cta_label. Use for non-CTA events such as
 * landing_share_card_clicked or share_card_referral.
 */
export function trackEvent(eventName, props = {}) {
  try {
    if (typeof window === "undefined" || !window.posthog) return;
    if (typeof window.posthog.capture !== "function") return;
    window.posthog.capture(eventName, {
      page_path: window.location?.pathname || "/",
      device: detectDevice(),
      timestamp_iso: new Date().toISOString(),
      ...props,
    });
  } catch {
    // Analytics must never break the user flow.
  }
}

// ─────────────────────────────────────────────────────────────────────
// FUNNEL HELPERS
// One canonical entry point per wedge product. Fires both PostHog (where
// the founder gets time-series + funnel UI for free) AND a backend mirror
// (so internal counts are visible even before PostHog is fully wired).
// ─────────────────────────────────────────────────────────────────────

/** All defined funnels — single source of truth so stages stay consistent. */
export const FUNNELS = {
  best_match: {
    label: "Best Match Quiz",
    stages: ["viewed", "started", "completed", "result_clicked", "messaged_match"],
  },
  practice_review: {
    label: "Practice Review",
    stages: ["viewed", "upload_started", "upload_completed", "analysis_completed", "focus_clicked"],
  },
  standards_check: {
    label: "Standards Check Ready",
    stages: ["viewed", "date_set", "practice_review_clicked", "ready_state_reached"],
  },
  cpd: {
    label: "CPD Record",
    stages: ["viewed", "cert_downloaded", "engaged_followup"],
  },
  signup: {
    label: "Signup",
    stages: ["viewed", "started", "completed"],
  },
  subscribe: {
    label: "Subscribe",
    stages: ["pricing_viewed", "plan_selected", "checkout_started", "completed"],
  },
  // Homepage section engagement (entered → dwelled ≥ 1.5s → CTA clicked).
  // The specific section name is in extras.section.
  homepage: {
    label: "Homepage Sections",
    stages: ["entered", "dwelled", "cta_clicked"],
  },
};

let _backendMirrorBaseUrl = null;
/** Wire once near app boot if you want server-side mirror counts.
 *  Without this, funnel events fire to PostHog only. */
export function configureFunnelMirror(apiBaseUrl) {
  _backendMirrorBaseUrl = apiBaseUrl || null;
}

async function _mirrorToBackend(payload) {
  if (!_backendMirrorBaseUrl) return;
  try {
    await fetch(`${_backendMirrorBaseUrl}/api/analytics/funnel`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true, // survives nav-away
    });
  } catch {
    // Best-effort.
  }
}

/**
 * Fire a funnel-stage event.
 *
 * @param {keyof typeof FUNNELS} funnel  Funnel key (must exist in FUNNELS)
 * @param {string} stage                  Stage name (must exist in FUNNELS[funnel].stages)
 * @param {Object} [props]                Extra event-specific props
 */
export function trackFunnel(funnel, stage, props = {}) {
  // Defensive — never throw from analytics.
  try {
    const def = FUNNELS[funnel];
    const stages = def?.stages || [];
    const stageIndex = stages.indexOf(stage);

    const payload = {
      funnel_name: funnel,
      stage_name: stage,
      stage_index: stageIndex >= 0 ? stageIndex : null,
      stage_total: stages.length || null,
      page_path: typeof window !== "undefined" ? window.location?.pathname || "/" : "/",
      device: detectDevice(),
      timestamp_iso: new Date().toISOString(),
      ...props,
    };

    // 1) PostHog
    if (typeof window !== "undefined" && window.posthog?.capture) {
      window.posthog.capture(`funnel_${funnel}_${stage}`, payload);
    }

    // 2) Backend mirror (best-effort, fire-and-forget)
    _mirrorToBackend(payload);
  } catch {
    // Silent.
  }
}

export default trackCta;
